import { queryClient } from 'v2/configs/rq-setup'
import type { RAccountDetails } from 'v2/pages/billing/types'
import { getAccountId } from 'v2/pages/billing/utils'
import { coreService } from 'v2/shared/services/base-services'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { getLocalAuth } from 'v2/utils'

export interface RaiseTicketArgs {
  subject: string
  description: string
  // Caller-supplied CC teammates. Core (SER-5507) validates these server-side:
  // same company domain as the requester only, deduped, capped at 5. Anything
  // off-domain is silently dropped, so we send as-is without client validation.
  ccEmails?: string[]
}

// Shape returned by contacto-core `/v1/feedback/escalate`. All fields beyond the
// Slack ids can be null when Pylon creation fails (Slack escalation still succeeds).
export interface REscalateResponse {
  api_id?: string
  thread_ts?: string
  channel_id?: string
  is_followup?: boolean
  pylon_ticket_id?: string | null
  pylon_contact_id?: string | null
}

const MAX_SUBJECT_LENGTH = 120

// Marker appended to the body so CX can tell these tickets skipped Buddy's AI
// triage (no classifier/structured gathering) and may need manual qualification.
const DIRECT_TICKET_TAG = 'Raised via Buddy /ticket command (direct, no AI triage).'

// raiseTicket: POST a support escalation straight to contacto-core, bypassing the
// Buddy LLM loop. Goes through the regional gateway as
// /v1/contacto-core/feedback/escalate; hodor injects the core admin Basic auth +
// AOM_UUID header, so the account is resolved server-side from AOM_UUID. The
// browser only supplies the issue text + best-effort context.
export const raiseTicket = async ({
  subject,
  description,
  ccEmails,
}: RaiseTicketArgs): Promise<REscalateResponse> => {
  const auth = getLocalAuth()
  const accountDetails = queryClient.getQueryData<RAccountDetails>(
    billingQueryKeys().accountDetails(getAccountId()),
  )
  const account = accountDetails?.account

  const trimmedSubject = subject.trim().slice(0, MAX_SUBJECT_LENGTH)
  const trimmedDescription = description.trim()

  // Hard stop at the service boundary: never POST an empty escalation body even if
  // a caller bypasses the dialog's submit-disabled guard.
  if (!trimmedDescription) {
    throw new Error('raiseTicket: description is required')
  }

  const body = {
    subject: trimmedSubject || `Support request from ${auth?.name || auth?.email || 'customer'}`,
    message: `${trimmedDescription}\n\n— ${DIRECT_TICKET_TAG}`,
    // origin + pathname only — query/fragment can carry tokens/emails/ids that
    // shouldn't flow into support tooling.
    page_url: `${window.location.origin}${window.location.pathname}`,
    context: {
      region: auth?.region,
      country_id: account?.country_id,
      roles: auth?.roles,
      // Core reads context.ip_address as the reported client IP; registered_ip is
      // the closest signal the console holds (signup IP).
      ip_address: account?.registered_ip,
    },
    ...(ccEmails && ccEmails.length > 0 ? { cc_emails: ccEmails } : {}),
  }

  return coreService.post<REscalateResponse>('/feedback/escalate', body)
}
